import React, { useState } from 'react'
import { proposeStories } from '../lib/claude'

// Finder results survive closing the modal (and page reloads) for the browser session.
function readSession(key, fallback) {
  try {
    const saved = sessionStorage.getItem(key)
    return saved ? JSON.parse(saved) : fallback
  } catch { return fallback }
}

function writeSession(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)) } catch { /* storage unavailable */ }
}

export default function StoryFinder({ onClose, onAddStory }) {
  const [results, setResults] = useState(() => readSession('im_finder_results', null))
  const [prevResults, setPrevResults] = useState(() => readSession('im_finder_prev', null))
  const [addedAngles, setAddedAngles] = useState(() => readSession('im_finder_added', {}))
  const [searching, setSearching] = useState(false)
  const [activeSection, setActiveSection] = useState('special_features')
  const [selectedAscents, setSelectedAscents] = useState([])
  const [dragAscent, setDragAscent] = useState(null)
  const [dragOverAscent, setDragOverAscent] = useState(null)

  async function search() {
    setSearching(true)
    if (results) {
      setPrevResults(results)
      writeSession('im_finder_prev', results)
    }
    try {
      const fresh = await proposeStories()
      setResults(fresh)
      writeSession('im_finder_results', fresh)
      setSelectedAscents([])
    } catch (e) {
      console.error('Story search failed:', e)
    }
    setSearching(false)
  }

  async function handleAngleClick(proposal, angle, section) {
    const key = proposal.headline + '|' + angle.label
    if (addedAngles[key]) return
    const storyType = section === 'special_features' ? 'special'
      : section === 'notable_ascents' ? 'notable_ascent'
      : section === 'gear' ? 'gear'
      : 'regular'
    const isRubric = section === 'regular'
    await onAddStory({ ...proposal, story_type: storyType }, angle, isRubric)
    const updated = { ...addedAngles, [key]: true }
    setAddedAngles(updated)
    writeSession('im_finder_added', updated)
  }

  function toggleAscent(proposal) {
    setSelectedAscents(prev => {
      const exists = prev.find(p => p.headline === proposal.headline)
      if (exists) return prev.filter(p => p.headline !== proposal.headline)
      // New selections go to top of list
      return [proposal, ...prev]
    })
  }

  // Drag reorder within selected list
  function handleAscentDragStart(proposal) { setDragAscent(proposal) }
  function handleAscentDragEnd() { setDragAscent(null); setDragOverAscent(null) }
  function handleAscentDragOver(e, proposal) {
    e.preventDefault()
    if (!dragAscent || dragAscent.headline === proposal.headline) return
    setDragOverAscent(proposal.headline)
    setSelectedAscents(prev => {
      const from = prev.findIndex(p => p.headline === dragAscent.headline)
      const to = prev.findIndex(p => p.headline === proposal.headline)
      if (from === -1 || to === -1) return prev
      const next = [...prev]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next
    })
  }

  async function createAscentsCard() {
    if (selectedAscents.length === 0) return
    // Build bullets in the correct format: **Bold title:** description text
    const bulletText = selectedAscents
      .map(p => {
        const desc = p.description || p.summary || ''
        return '- **' + p.headline + ':** ' + desc
      })
      .join('\n')
    const combined = {
      sport_slug: 'NOTABLE ASCENTS',
      headline: 'Notable ascents, descents and race results',
      story_type: 'notable_ascent',
      proposed_angles: [],
      angles: [{ label: 'Results list', description: bulletText }]
    }
    await onAddStory(combined, combined.angles[0], false)
    setSelectedAscents([])
  }

  const SECTIONS = [
    { key: 'special_features', label: 'Special Features', accent: '#B33F1F' },
    { key: 'notable_ascents', label: 'Notable Ascents', accent: '#767676' },
    { key: 'gear', label: 'Gear Wall', accent: '#373429' },
    { key: 'regular', label: 'Regular Features', accent: '#5E80FB' }
  ]

  const currentSection = SECTIONS.find(s => s.key === activeSection)
  const currentProposals = results?.[activeSection] || []
  const prevProposals = prevResults?.[activeSection] || []

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="finder-modal" onClick={e => e.stopPropagation()}>
        <div className="finder-header">
          <h2 className="finder-title">Story Finder</h2>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button className="btn-primary" onClick={search} disabled={searching}>
              {searching ? 'Searching…' : '🔍 Search'}
            </button>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Section tabs */}
        <div className="finder-tabs">
          {SECTIONS.map(s => (
            <button key={s.key}
              className={'finder-tab' + (activeSection === s.key ? ' finder-tab--active' : '')}
              style={activeSection === s.key ? { borderBottomColor: s.accent, color: s.accent } : {}}
              onClick={() => setActiveSection(s.key)}
            >{s.label}</button>
          ))}
        </div>

        {/* Selected ascents reorder panel */}
        {activeSection === 'notable_ascents' && selectedAscents.length > 0 && (
          <div className="finder-ascents-bar">
            <div className="finder-ascents-selected">
              <p className="finder-ascents-count">{selectedAscents.length} selected — drag to reorder</p>
              <div className="finder-ascents-reorder">
                {selectedAscents.map((p, i) => (
                  <div key={p.headline}
                    className={'finder-ascent-chip' + (dragOverAscent === p.headline ? ' drag-over-chip' : '')}
                    draggable
                    onDragStart={() => handleAscentDragStart(p)}
                    onDragEnd={handleAscentDragEnd}
                    onDragOver={e => handleAscentDragOver(e, p)}
                  >
                    <span className="finder-ascent-chip-num">{i + 1}</span>
                    <span className="finder-ascent-chip-text">{p.headline}</span>
                    <span className="finder-ascent-chip-handle">⠿</span>
                  </div>
                ))}
              </div>
            </div>
            <button className="btn-primary" onClick={createAscentsCard} style={{ flexShrink: 0 }}>
              Create card
            </button>
          </div>
        )}

        {!results && !searching && (
          <div className="finder-empty"><p>Click Search to find this week's story candidates.</p></div>
        )}
        {searching && <div className="loading-state"><div className="loading-spinner" /><p>Searching…</p></div>}

        <div className="finder-list">
          {currentProposals.length > 0 && prevProposals.length > 0 && (
            <p className="finder-section-label">This search</p>
          )}
          {currentProposals.map((proposal, i) => (
            <ProposalCard key={'cur-' + i} proposal={proposal} section={activeSection}
              isNew={true} addedAngles={addedAngles} onAngleClick={handleAngleClick}
              accent={currentSection.accent}
              isAscent={activeSection === 'notable_ascents'}
              isAscentSelected={selectedAscents.some(p => p.headline === proposal.headline)}
              onAscentToggle={() => toggleAscent(proposal)} />
          ))}
          {prevProposals.length > 0 && (
            <>
              <p className="finder-section-label" style={{ color: '#c8c0b8' }}>Previous search</p>
              {prevProposals.map((proposal, i) => (
                <ProposalCard key={'prev-' + i} proposal={proposal} section={activeSection}
                  isNew={false} addedAngles={addedAngles} onAngleClick={handleAngleClick}
                  accent={currentSection.accent}
                  isAscent={activeSection === 'notable_ascents'}
                  isAscentSelected={selectedAscents.some(p => p.headline === proposal.headline)}
                  onAscentToggle={() => toggleAscent(proposal)} />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function ProposalCard({ proposal, section, isNew, addedAngles, onAngleClick, accent, isAscent, isAscentSelected, onAscentToggle }) {
  if (isAscent) {
    return (
      <div
        className={'finder-proposal finder-ascent' + (!isNew ? ' finder-proposal--old' : '') + (isAscentSelected ? ' finder-ascent--selected' : '')}
        onClick={onAscentToggle}
      >
        <div className="finder-ascent-check">
          {isAscentSelected ? '✓' : ''}
        </div>
        <div style={{ flex: 1 }}>
          <h3 className="finder-headline" style={{ fontSize: 'calc(0.85rem * var(--scale))', marginBottom: '0.25rem' }}>
            {proposal.headline}
          </h3>
          {(proposal.description || proposal.summary) && (
            <p className="finder-summary" style={{ marginBottom: 0, color: isAscentSelected ? '#444' : undefined }}>
              {proposal.description || proposal.summary}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={'finder-proposal' + (!isNew ? ' finder-proposal--old' : '')}>
      <div className="finder-proposal-header">
        <span className="proposal-slug" style={{ color: accent }}>{proposal.sport_slug}</span>
        {proposal.rubric_type && <span className="finder-rubric-tag">{proposal.rubric_type}</span>}
        {proposal.gear_type && <span className="finder-rubric-tag">{proposal.gear_type}</span>}
      </div>
      <h3 className="finder-headline">{proposal.headline}</h3>
      {proposal.summary && <p className="finder-summary">{proposal.summary}</p>}
      {proposal.description && <p className="finder-summary">{proposal.description}</p>}
      <div className="finder-angles">
        {(proposal.angles || []).map((angle, j) => {
          const key = proposal.headline + '|' + angle.label
          const added = addedAngles[key]
          return (
            <button key={j}
              className={'finder-angle-btn' + (added ? ' finder-angle-btn--added' : '')}
              style={added ? { borderColor: accent, background: accent + '14' } : {}}
              onClick={() => onAngleClick(proposal, angle, section)}
              disabled={added}>
              <strong style={added ? { color: accent } : {}}>{angle.label}</strong>
              <span>{angle.description}</span>
              {added && <span className="angle-added-tick" style={{ color: accent }}>✓ Added</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}
